import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import tr from "./tr";
import en from "./en";
import de from "./de";

const DICTS = { tr, en, de };
export const LANGS = ["tr", "en", "de"];
const LOCALES = { tr: "tr-TR", en: "en-US", de: "de-DE" };
const I18nCtx = createContext(null);

function lookup(dict, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

// Saved choice wins; otherwise suggest from the browser's preferred languages; Turkish is the default.
function initialLang() {
  const saved = localStorage.getItem("lang");
  if (LANGS.includes(saved)) return saved;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
  for (const p of prefs) {
    const base = String(p).toLowerCase().split("-")[0];
    if (LANGS.includes(base)) return base;
  }
  return "tr";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  // Increments only when the visitor picks a language themselves (used by pages whose URL is language-specific).
  const [userSwitch, setUserSwitch] = useState(0);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  // Native form validation bubbles follow the browser language; replace them with the interface language.
  useEffect(() => {
    const msg = (k, vars) => {
      let s = lookup(DICTS[lang], `form.${k}`);
      Object.entries(vars || {}).forEach(([a, b]) => (s = s.replaceAll(`{${a}}`, b)));
      return s;
    };
    const onInvalid = (e) => {
      const el = e.target;
      if (!el.validity || !el.setCustomValidity) return;
      el.setCustomValidity("");
      const v = el.validity;
      let text = msg("invalid");
      if (v.valueMissing) text = msg("required");
      else if (v.typeMismatch && el.type === "email") text = msg("email");
      else if (v.tooShort) text = msg("tooShort", { n: el.minLength });
      else if (v.rangeUnderflow || v.rangeOverflow) text = msg("range", { min: el.min, max: el.max });
      el.setCustomValidity(text);
    };
    const onInput = (e) => e.target.setCustomValidity && e.target.setCustomValidity("");
    document.addEventListener("invalid", onInvalid, true);
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    return () => {
      document.removeEventListener("invalid", onInvalid, true);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
    };
  }, [lang]);
  const setLang = useCallback((l, byUser = false) => {
    if (!LANGS.includes(l)) return;
    localStorage.setItem("lang", l);
    setLangState(l);
    if (byUser) setUserSwitch((n) => n + 1);
  }, []);
  const value = useMemo(() => {
    const locale = LOCALES[lang];
    const t = (key, vars) => {
      let s = lookup(DICTS[lang], key);
      if (s === undefined) s = lookup(DICTS.en, key) ?? key;
      if (typeof s === "string" && vars) Object.entries(vars).forEach(([k, v]) => (s = s.replaceAll(`{${k}}`, v)));
      return s;
    };
    const fmtDate = (d, withTime = false) =>
      d ? new Intl.DateTimeFormat(locale, withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" }).format(new Date(d)) : "—";
    const fmtMoney = (n, cur = "TRY") => new Intl.NumberFormat(locale, { style: "currency", currency: cur, currencyDisplay: "narrowSymbol" }).format(n || 0);
    const fmtNum = (n) => new Intl.NumberFormat(locale).format(n || 0);
    const fmtBytes = (b) => {
      if (!b && b !== 0) return "—";
      const u = ["B", "KB", "MB", "GB"];
      let i = 0, v = b;
      while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(v)} ${u[i]}`;
    };
    const errMsg = (e) => {
      const d = e?.response?.data?.detail;
      if (typeof d === "string" && lookup(DICTS[lang].errors, d)) return t(`errors.${d}`);
      if (Array.isArray(d)) return t("errors.VALIDATION");
      return t("errors.GENERIC");
    };
    return { lang, setLang, userSwitch, t, fmtDate, fmtMoney, fmtNum, fmtBytes, errMsg };
  }, [lang, setLang, userSwitch]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
